import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-3">
            Browse Product Categories
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
            Select a category below to explore detailed product spreadsheets and comparison guides on our main platform.
          </p>
        </div>

        <div className="flex justify-center mb-12">
          <div ref={dropdownRef} className="relative w-full max-w-md">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm cursor-pointer hover:border-primary-300 transition-colors"
            >
              <span className={selectedCategory ? 'text-foreground-900' : 'text-foreground-400'}>
                {selectedCategory || '— Select a Category —'}
              </span>
              <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-foreground-500"></i>
              </span>
            </button>
            {isOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200 rounded-lg shadow-lg z-40 max-h-72 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => {
                      setSelectedCategory(cat.name);
                      setIsOpen(false);
                    }}
                    className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-foreground-400`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mb-12 rounded-xl overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20curated%20fashion%20product%20display%20with%20organized%20arrangement%20of%20sneakers%2C%20hoodies%2C%20caps%2C%20bags%20and%20accessories%20on%20clean%20warm%20beige%20background%2C%20boutique%20retail%20display%20aesthetic%2C%20soft%20diffused%20studio%20lighting%2C%20neutral%20cream%20and%20sand%20color%20palette%20with%20subtle%20warm%20accents%2C%20minimal%20clean%20composition%2C%20premium%20product%20photography%2C%20sophisticated%20shopping%20atmosphere%2C%20editorial%20catalogue%20style%2C%20serene%20organized%20visual%20merchandising&width=1440&height=400&seq=category-banner-fashion-2026&orientation=landscape"
            alt="Hipobuy Sheet product categories collection"
            className="w-full h-48 md:h-64 object-cover object-top"
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col items-center gap-3 p-4 md:p-5 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 hover:bg-background-50 transition-all cursor-pointer"
            >
              <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-500 transition-colors">
                <i className={`${cat.icon} text-lg md:text-xl`}></i>
              </span>
              <span className="text-xs md:text-sm text-foreground-700 font-medium text-center whitespace-nowrap">
                {cat.name}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}